import styled from 'styled-components'

export const CardContainer = styled.div<{
	$isDragging?: boolean
	$isOver?: boolean
}>`
	background: #ffffff;
	border: 1px solid #e1e5e9;
	border-radius: 8px;
	padding: 1.1rem 1.1rem 1rem;
	cursor: grab;
	transition: all 0.2s ease;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
	position: relative;
	touch-action: none;
	display: flex;
	flex-direction: column;
	gap: 0.8rem;

	&:hover {
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
		transform: translateY(-1px);
	}

	${props =>
		props.$isDragging &&
		`
    opacity: 0.5;
    transform: rotate(5deg);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.25);
    z-index: 1000;
  `}

	${props =>
		props.$isOver &&
		`
    background: #f8f9ff;
    border-color: #007bff;
    box-shadow: 0 4px 12px rgba(0, 123, 255, 0.15);
  `}

	&:active {
		cursor: grabbing;
	}
`

export const CardHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
`

export const CardTitle = styled.h4`
	margin: 0;
	font-size: 1rem;
	font-weight: 600;
	color: #333;
	flex: 1;
	line-height: 1.4;
`

export const CardActions = styled.div`
	display: flex;
	gap: 0.25rem;
	opacity: 0;
	transition: opacity 0.2s ease;

	${CardContainer}:hover & {
		opacity: 1;
	}
`

export const CardButton = styled.button`
	background: none;
	border: none;
	padding: 0.25rem;
	border-radius: 4px;
	cursor: pointer;
	font-size: 0.875rem;
	transition: all 0.2s ease;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 24px;
	height: 24px;

	&:hover {
		background-color: #f5f5f5;
	}

	&.edit:hover {
		background-color: #e3f2fd;
	}

	&.delete:hover {
		background-color: #ffebee;
	}
`

export const CardDescription = styled.p`
	margin: 0;
	font-size: 0.875rem;
	color: #666;
	line-height: 1.4;
`

export const CardMeta = styled.div`
	display: flex;
	align-items: center;
	gap: 0.5rem;
	font-size: 0.75rem;
	color: #888;
`

export const TimeSlot = styled.span`
	background-color: #f0f0f0;
	padding: 0.125rem 0.375rem;
	border-radius: 12px;
	font-weight: 500;
`

export const CardForm = styled.form`
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
`

export const FormInput = styled.input`
	padding: 0.5rem;
	border: 1px solid #ddd;
	border-radius: 4px;
	font-size: 1rem;
	transition: border-color 0.2s ease;

	&:focus {
		outline: none;
		border-color: #007bff;
	}
`

export const FormTextarea = styled.textarea`
	padding: 0.5rem;
	border: 1px solid #ddd;
	border-radius: 4px;
	font-size: 0.875rem;
	min-height: 60px;
	resize: vertical;
	transition: border-color 0.2s ease;
	font-family: inherit;

	&:focus {
		outline: none;
		border-color: #007bff;
	}
`

export const FormActions = styled.div`
	display: flex;
	gap: 0.5rem;
	justify-content: flex-end;
`

export const FormButton = styled.button`
	padding: 0.5rem 1rem;
	border: 1px solid #ddd;
	border-radius: 4px;
	font-size: 0.875rem;
	cursor: pointer;
	transition: all 0.2s ease;

	&.primary {
		background-color: #007bff;
		color: white;
		border-color: #007bff;

		&:hover {
			background-color: #0056b3;
			border-color: #0056b3;
		}
	}

	&.secondary {
		background-color: #f8f9fa;
		color: #6c757d;

		&:hover {
			background-color: #e9ecef;
		}
	}
`

export const CardLocation = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.3rem;
`

export const LocationHeader = styled.div`
	display: flex;
	align-items: center;
	gap: 0.3rem;
	font-size: 0.8rem;
	font-weight: 600;
	color: #374151;

	svg {
		width: 16px;
		height: 16px;
		flex-shrink: 0;
		fill: currentColor;
	}
`

export const LocationText = styled.div`
	font-size: 0.75rem;
	color: #6b7280;
	line-height: 1.4;
	padding-left: calc(16px + 0.3rem);
`

export const LocationMapLink = styled.a`
	font-size: 0.72rem;
	color: #ea580c;
	font-weight: 600;
	text-decoration: none;
	padding-left: calc(16px + 0.3rem);
	width: fit-content;

	&:hover {
		text-decoration: underline;
	}
`

export const LinksHeader = styled.div`
	display: flex;
	align-items: center;
	gap: 0.3rem;
	font-size: 0.8rem;
	font-weight: 600;
	color: #374151;

	svg {
		width: 16px;
		height: 16px;
		flex-shrink: 0;
		fill: currentColor;
	}
`

export const CardLinks = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.6rem;
	cursor: default;
	pointer-events: auto;
	position: relative;
	z-index: 1;
`

export const LinkPreviewCard = styled.a`
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 6px;
	border: 1px solid #e5e7eb;
	border-radius: 6px;
	background: #f9fafb;
	text-decoration: none;
	transition: all 0.2s;
	cursor: pointer;
	overflow: hidden;

	&:hover {
		background: #f3f4f6;
		border-color: #f97316;
		transform: translateX(2px);
	}
`

export const LinkThumbnail = styled.div<{ src?: string }>`
	width: 45px;
	height: 45px;
	min-width: 45px;
	border-radius: 4px;
	background: ${props => props.src ? `url(${props.src})` : '#e5e7eb'};
	background-size: cover;
	background-position: center;
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
`

export const LinkInfo = styled.div`
	flex: 1;
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 2px;
`

export const LinkSource = styled.div`
	display: flex;
	align-items: center;
	gap: 4px;
	font-size: 0.65rem;
	font-weight: 600;
	color: #6b7280;
	text-transform: capitalize;
`

export const LinkUrl = styled.div`
	font-size: 0.7rem;
	color: #3b82f6;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`
